/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/DestructiveConfirmDialog.tsx
 * PURPOSE: Shared Flowbite modal for irreversible CRM actions (replaces native confirm for shell CTAs)
 * DEPENDENCIES: react, flowbite-react Modal
 * PUBLIC_API: DestructiveConfirmDialog, DestructiveConfirmDialogProps
 * SIDE_EFFECTS: Restores keyboard focus to the trigger element after Cancel or Escape; Enter does not confirm unless the destructive control is focused
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 152/200
 * RECENT: 2026-05-11 — Cancel outline or ghost plus border token; destructive filled **`var(--color-error)`** fallback; focus return on dismiss; Enter guard (GitHub #85)
 * RECENT_PRIOR: 2026-05-08 [2.2.878] Docs sync — root **`CHANGELOG` [2.2.878]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.874] Modal **`theme.root.base`** uses **`z-[140]`** so confirm appears above post-call (`z-[120]`)
 */

import type { ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'flowbite-react';

export type DestructiveConfirmDialogProps = {
  open: boolean;
  title: string;
  /** Plain string (default typography) or rich layout (e.g. deal name emphasis). */
  message: string | ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  /** Override default `data-testid` on the Cancel button (e.g. deal delete flow). */
  cancelButtonTestId?: string;
  /** Override default `data-testid` on the confirm button. */
  submitButtonTestId?: string;
};

export function DestructiveConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  busy = false,
  onCancel,
  onConfirm,
  cancelButtonTestId = 'crm-destructive-confirm-cancel',
  submitButtonTestId = 'crm-destructive-confirm-submit',
}: DestructiveConfirmDialogProps) {
  const body =
    typeof message === 'string' ? (
      <p
        className="text-sm text-gray-700 dark:text-gray-300"
        data-testid="crm-destructive-confirm-message"
      >
        {message}
      </p>
    ) : (
      message
    );

  /** Element that had focus when the modal opened — restored after Cancel or Escape (not after Confirm). */
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const prevOpenRef = useRef(false);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (open && !prevOpenRef.current) {
      const ae = document.activeElement;
      returnFocusRef.current = ae instanceof HTMLElement ? ae : null;
    }
    prevOpenRef.current = open;
  }, [open]);

  const restoreFocusAfterDismiss = useCallback(() => {
    const el = returnFocusRef.current;
    returnFocusRef.current = null;
    queueMicrotask(() => {
      if (!el || typeof el.focus !== 'function') return;
      if (!document.body.contains(el)) return;
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    });
  }, []);

  const handleDismiss = useCallback(() => {
    restoreFocusAfterDismiss();
    onCancel();
  }, [onCancel, restoreFocusAfterDismiss]);

  const handleConfirmClick = useCallback(() => {
    returnFocusRef.current = null;
    void onConfirm();
  }, [onConfirm]);

  /** Enter must not activate confirm unless focus is on the destructive control (ADR: accidental submit). */
  const handleEnterGuardCapture = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' || busy) return;
      if (e.defaultPrevented) return;
      const submitEl = submitButtonRef.current;
      const active = document.activeElement;
      if (submitEl && (active === submitEl || submitEl.contains(active))) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    },
    [busy]
  );

  return (
    <Modal
      show={open}
      onClose={handleDismiss}
      size="md"
      onKeyDownCapture={handleEnterGuardCapture}
      theme={{
        root: {
          /** Full `root.base` so merge never drops `fixed` or sizing; `z-[140]` clears `PostCallSummary` (`z-[120]`). */
          base:
            'fixed inset-x-0 top-0 z-[140] h-screen overflow-y-auto overflow-x-hidden md:inset-0 md:h-full',
        },
      }}
    >
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>{body}</ModalBody>
      <ModalFooter className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          outline
          color="gray"
          size="sm"
          className="min-h-[44px] border-[color:var(--color-border,#d1d5db)] bg-transparent font-normal shadow-none dark:border-gray-500 dark:bg-transparent"
          onClick={handleDismiss}
          disabled={busy}
          data-testid={cancelButtonTestId}
        >
          Cancel
        </Button>
        <Button
          ref={submitButtonRef}
          type="button"
          size="sm"
          className="min-h-[44px] rounded-lg bg-[var(--color-error,#dc2626)] px-3 text-sm font-semibold !text-white shadow-sm hover:brightness-[0.94] focus:outline-none focus:ring-4 focus:ring-red-300 disabled:pointer-events-none disabled:opacity-50 dark:focus:ring-red-800 dark:hover:brightness-110"
          onClick={handleConfirmClick}
          disabled={busy}
          data-testid={submitButtonTestId}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
